import {
  MaterialPrice,
  MaterialPriceList,
  UpdateMaterialPriceRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { useQpmCalculationUpdateMaterialPriceMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import {
  FloatNumberInput,
  fMoney,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useEffect, useState } from "react";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs";

export type MaterialPriceListComponentProps = {
  calculationId: string;
  materialPriceList?: MaterialPriceList;
  monitors?: Record<string, MonitorSnapDTO>;
  totalCost: number;
};

export function MaterialPriceListComponent({
  calculationId,
  materialPriceList,
  monitors,
  totalCost,
}: MaterialPriceListComponentProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Material")}</TableHead>
          <TableHead className="text-right">
            <div className="flex items-center justify-end gap-1">
              {t("Anzahl")}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={t("Hinweis zur Anzahl")}
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className={tooltipContentClassName}>
                    <p>
                      {t(
                        "Die Anzahl zeigt neue Monitore, die in den Materialkosten berechnet werden.",
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="text-right">
            <div className="flex items-center justify-end gap-1">
              {t("Altbestand")}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label={t("Hinweis zum Altbestand")}
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className={tooltipContentClassName}>
                    <p>
                      {t(
                        "Altbestand ist bereits vorhanden und wird nicht in den Materialkosten berechnet.",
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableHead>
          <TableHead className="text-right">{t("Preis pro Stück")}</TableHead>
          <TableHead className="text-right">{t("Gesamtpreis")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {materialPriceList &&
          monitors &&
          materialPriceList.positions.map((material) => (
            <MaterialPriceRow
              key={material.monitorId}
              calculationId={calculationId}
              material={material}
              monitor={monitors[material.monitorId]}
            />
          ))}
        <TableRow>
          <TableCell colSpan={4} className="font-bold">
            {t("Gesamt")}
          </TableCell>
          <TableCell className="text-end">{fMoney(totalCost)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export type MaterialPriceRowProps = {
  calculationId: string;
  material: MaterialPrice;
  monitor?: MonitorSnapDTO;
};

function MaterialPriceRow({
  calculationId,
  material,
  monitor,
}: MaterialPriceRowProps) {
  const [amount, setAmount] = useState(material.amount);
  const [price, setPrice] = useState(material.basePrice);
  const [request, setRequest] = useState<UpdateMaterialPriceRequest>({
    calculationId,
    basePrice: material.basePrice,
    monitorId: material.monitorId,
  });

  const [setMaterialPrice, { isLoading, error, reset, isSuccess }] =
    useQpmCalculationUpdateMaterialPriceMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setMaterialPrice,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  useEffect(() => {
    // Update amount if the position prop changes
    setAmount(material.amount);
  }, [material.amount]);

  useEffect(() => {
    // Update price if the position prop changes
    setPrice(material.basePrice);
  }, [material.basePrice]);

  return (
    <TableRow>
      <TableCell className="py-2 font-medium">
        {monitor?.name || t("NA")}
      </TableCell>
      <TableCell className="py-2 text-right">{amount}</TableCell>
      <TableCell className="py-2 text-right">
        {material.existingStockAmount ?? 0}
      </TableCell>
      <TableCell className="py-2 text-right">
        <FloatNumberInput
          className="justify-self-end"
          value={price}
          fractions={2}
          onChange={(val) => {
            setRequest({
              ...request,
              basePrice: val,
            });
          }}
          disabled={isLoading}
        />
      </TableCell>
      <TableCell className="py-2 text-right">
        <Value value={material.totalPrice} />
      </TableCell>
    </TableRow>
  );
}
